import { AuthStore } from '@app/core/state/auth.store';
import { effect, Injectable } from '@angular/core';
import { LoggingService } from '@app/core/services/logging.service';
import { UploadSessionItem } from '@modules/reconciliation-manager/models';
import { signal, WritableSignal } from '@angular/core';
// import { GlobalStateStore } from '../state';

export interface SessionInfo {
    sessionId: string;
    sessionClientId: string;
    sessionDate: string;
    sessionDateUtc: string;
    sessionUser: string;
    sessionTimeZoneIana?: string;
    sessionOffsetMinutes?: number | null;
}

export interface SessionFileBadgeState {
    registered: boolean;
    mappingRequired: boolean;
}

export interface UploadSessionViewState {
    currentPage: number;
    selectedSessionId: string | null;
}

export type ReconciliationNavMode = 'run-reconciliation' | 'create-sessions' | 'process-files';

@Injectable({
    providedIn: 'root',
})
export class SessionInfoStateStoreService {
    constructor(
        // private globalStateStore: GlobalStateStore,
        private loggingService: LoggingService,
        private authStore: AuthStore
        ) {
        effect(() => {
            const activeSession = this._activeUploadSession();

            if (activeSession) {
                this.loggingService.debug(
                    '\n***** GlobalStateStore: Active Upload Session updated',
                    activeSession
                );
            } else {
                this.loggingService.debug('GlobalStateStore: Active Upload Session cleared');
            }

        });
    }

    private _activeUploadSession = signal<UploadSessionItem | null>(null);
    private _activeUploadSessionByMode = signal<Record<ReconciliationNavMode, UploadSessionItem | null>>({
        'run-reconciliation': null,
        'create-sessions': null,
        'process-files': null,
    });
    private _activeReconciliationMode = signal<ReconciliationNavMode>('create-sessions');
    private _uploadSessionData: WritableSignal<UploadSessionItem[]> =
    signal<UploadSessionItem[]>([]);
    private _openSessionCountByUser = signal<Record<string, number>>({});
    private _sessionFileBadgeState: WritableSignal<
        Record<string, Record<string, SessionFileBadgeState>>
    > = signal<Record<string, Record<string, SessionFileBadgeState>>>({});
    private _uploadSessionViewState = signal<UploadSessionViewState>({
        currentPage: 1,
        selectedSessionId: null,
    });
    private _uploadSessionViewStateByMode = signal<Record<ReconciliationNavMode, UploadSessionViewState>>({
        'run-reconciliation': { currentPage: 1, selectedSessionId: null },
        'create-sessions': { currentPage: 1, selectedSessionId: null },
        'process-files': { currentPage: 1, selectedSessionId: null },
    });
    private _activeSessionLastUpdatedByMode = signal<Record<ReconciliationNavMode, string | null>>({
        'run-reconciliation': null,
        'create-sessions': null,
        'process-files': null,
    });

    // Public readonly signal for other components to observe
    readonly activeUploadSession = this._activeUploadSession.asReadonly();
    readonly activeUploadSessionByMode = this._activeUploadSessionByMode.asReadonly();
    readonly activeReconciliationMode = this._activeReconciliationMode.asReadonly();
    readonly uploadSessionData = this._uploadSessionData.asReadonly();
    readonly openSessionCountByUser = this._openSessionCountByUser.asReadonly();
    readonly sessionFileBadgeState = this._sessionFileBadgeState.asReadonly();
    readonly uploadSessionViewState = this._uploadSessionViewState.asReadonly();
    readonly uploadSessionViewStateByMode = this._uploadSessionViewStateByMode.asReadonly();
    readonly activeSessionLastUpdatedByMode = this._activeSessionLastUpdatedByMode.asReadonly();

    setActiveReconciliationMode(mode: ReconciliationNavMode): void {
        this._activeReconciliationMode.set(mode);
        this._activeUploadSession.set(this._activeUploadSessionByMode()[mode] ?? null);
        this._uploadSessionViewState.set(this._uploadSessionViewStateByMode()[mode]);
    }

    getActiveUploadSessionValue(): UploadSessionItem | null {
        const mode = this._activeReconciliationMode();
        return this._activeUploadSessionByMode()[mode] ?? null;
    }

    getUploadSessionData(): UploadSessionItem[] {
        return this._uploadSessionData();
    }

    getSessionFileBadgeMap(sessionId: string): Record<string, SessionFileBadgeState> {
        return this._sessionFileBadgeState()[sessionId] ?? {};
    }

    setSessionFileBadgeMap(
        sessionId: string,
        map: Record<string, SessionFileBadgeState>
    ): void {
        this._sessionFileBadgeState.update((current) => ({
            ...current,
            [sessionId]: { ...map },
        }));
    }

    updateSessionFileBadgeState(
        sessionId: string,
        supplierName: string,
        state: SessionFileBadgeState
    ): void {
        if (!sessionId || !supplierName) {
            return;
        }
        this._sessionFileBadgeState.update((current) => {
            const sessionMap = current[sessionId] ?? {};
            return {
                ...current,
                [sessionId]: {
                    ...sessionMap,
                    [supplierName]: { ...state },
                },
            };
        });
    }

    setActiveUploadSessionId(session: UploadSessionItem | null) {
        const mode = this._activeReconciliationMode();
        this.setActiveUploadSessionForMode(mode, session);
    }

    getActiveSessionLastUpdatedForMode(
        mode: ReconciliationNavMode
    ): string | null {
        return this._activeSessionLastUpdatedByMode()[mode] ?? null;
    }

    setUploadSessionData(session: UploadSessionItem[]) {
        const safeSessions = Array.isArray(session) ? session : [];
        this._uploadSessionData.set(safeSessions);
        this._openSessionCountByUser.set(this.buildOpenSessionCountByUser(safeSessions));
    }

    getOpenSessionCountForUser(user: string): number {
        const normalizedUser = this.normalizeText(user);
        if (!normalizedUser) {
            return 0;
        }

        return this._openSessionCountByUser()[normalizedUser] ?? 0;
    }

    getUploadSessionViewState(): UploadSessionViewState {
        const mode = this._activeReconciliationMode();
        return this._uploadSessionViewStateByMode()[mode];
    }

    setUploadSessionViewState(state: Partial<UploadSessionViewState>): void {
        const mode = this._activeReconciliationMode();
        this.setUploadSessionViewStateForMode(mode, state);
    }

    setUploadSessionViewStateForMode(
        mode: ReconciliationNavMode,
        state: Partial<UploadSessionViewState>
    ): void {
        this._uploadSessionViewStateByMode.update((allStates) => {
            const current = allStates[mode];
            const next = {
                ...current,
                ...state,
                currentPage:
                    typeof state.currentPage === 'number' && state.currentPage > 0
                        ? Math.floor(state.currentPage)
                        : current.currentPage,
            };
            this._uploadSessionViewState.set(next);
            return {
                ...allStates,
                [mode]: next,
            };
        });
    }

    setActiveUploadSessionForMode(
        mode: ReconciliationNavMode,
        session: UploadSessionItem | null
    ): void {
        const validatedSession = session ? this.normalizeUploadSession(session) : null;

        this._activeUploadSessionByMode.update((state) => ({
            ...state,
            [mode]: validatedSession,
        }));
        this._activeSessionLastUpdatedByMode.update((state) => ({
            ...state,
            [mode]: this.getSessionLastUpdated(validatedSession),
        }));

        if (this._activeReconciliationMode() === mode) {
            this._activeUploadSession.set(validatedSession);
        }
    }

    getSessionInfo(): SessionInfo {
        const clientId = this.authStore.getClientValue();
        const userId   = this.authStore.getUserValue();
        const session = this.getActiveUploadSessionValue();

        const sessionId = session?.id ?? '';
        const sessionDate = session?.createdDate ?? '';
        const sessionDateUtc = session?.createdDateUtc ?? '';
        const sessionClientId = clientId ?? '';
        const sessionUser = userId ?? '';
        const sessionTimeZoneIana = session?.userTimeZoneIana;
        const sessionOffsetMinutes = session?.offsetMinutesAtUpload ?? null;

        return { sessionId, sessionClientId, sessionDate, sessionDateUtc, sessionUser, sessionTimeZoneIana, sessionOffsetMinutes };
    }

    updateSessionRow(sessionId: string, changes: Partial<UploadSessionItem>): void {
        this._uploadSessionData.update((list: UploadSessionItem[]) => {
            const index = list.findIndex((session) => session.id === sessionId);
            if (index === -1) {
                return list;
            }

            const updated = { ...list[index], ...changes };
            const nextList = [...list];
            nextList[index] = updated;

            return nextList;
        });
        this._openSessionCountByUser.set(
            this.buildOpenSessionCountByUser(this._uploadSessionData())
        );
    }

    private normalizeUploadSession(session: UploadSessionItem): UploadSessionItem {
        const displayResults = session.results ?? session.filename ?? '';

        return {
            ...session,
            results: displayResults,
            filename: session.filename ?? displayResults,
            total: Number(session.total),
            completeDate: session.completeDate || '',
            completeDateUtc: session.completeDateUtc || undefined,
            notes: session.notes || '',
        };
    }

    private getSessionLastUpdated(session: UploadSessionItem | null): string | null {
        if (!session) {
            return null;
        }

        return session.completeDateUtc
            || session.completeDate
            || session.createdDateUtc
            || session.createdDate
            || null;
    }

    private buildOpenSessionCountByUser(
        sessions: ReadonlyArray<UploadSessionItem>
    ): Record<string, number> {
        const countMap: Record<string, number> = {};

        sessions.forEach((session) => {
            const normalizedStatus = this.normalizeText(session?.status);
            if (normalizedStatus !== 'open') {
                return;
            }

            const sessionWithUserId = session as UploadSessionItem & {
                userId?: string | null;
            };
            const userKeys = [
                this.normalizeText(sessionWithUserId.user),
                this.normalizeText(sessionWithUserId.userId),
            ].filter((value, index, list) => value && list.indexOf(value) === index);

            userKeys.forEach((key) => {
                countMap[key] = (countMap[key] ?? 0) + 1;
            });
        });

        return countMap;
    }

    private normalizeText(value: unknown): string {
        return value?.toString().trim().toLowerCase() ?? '';
    }


}
